import { toCameraConfigurationPage, toUpdateCameraConfigurationRequestAPI, toUpdateSpeedLimitRequestAPI } from './cameraConfigurationTransformer';
import type { CameraConfigurationAPI } from './definitions';
import { gql } from '@apollo/client';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

import type {
  CameraConfiguration,
  FleetCameraEventsConfigurationInput,
  FleetEventsConfiguration,
  PageableContent,
  PageableContentAPI,
  Sort,
  UpdateCameraConfigurationRequest,
  UpdateCameraSpeedLimit,
  WifiSettingsList,
} from '@cobliteam/common-types';

import graphQLRequester from '../../request/GraphQLRequester';

export class GraphQLCameraConfigurationRequester {
  private queryGetCameraConfiguration = gql`
    query getCameraConfiguration($page: Int, $size: Int, $sort: [String]) {
      cameraConfiguration(page: $page, size: $size, sort: $sort) {
        content {
          audio_status
          device_id
          updated_at
          duty_type
          speed_limit_warning_in_kmph
        }
        empty
        first
        last
        number
        number_of_elements
        size
        total_elements
        total_pages
      }
    }
  `;

  private queryGetFleetCameraConfiguration = gql`
    query getFleetCameraConfiguration {
      fleetEventsConfiguration {
        updated_at
        updated_by_name
        camera_events_config {
          entire_fleet_camera_events_configuration {
            dms_events {
              min_speed_trigger_in_km_per_h
              distracted_driving {
                enabled
                sensibility
              }
              yawn {
                enabled
                sensibility
              }
              phone_usage {
                enabled
                sensibility
              }
              eyes_closed {
                enabled
                sensibility
              }
              smoking {
                enabled
                sensibility
              }
            }
            adas_events {
              min_speed_trigger_in_km_per_h
              forward_collision_warning {
                enabled
                sensibility
              }
            }
            harsh_events {
              light {
                acceleration {
                  enabled
                  sensibility
                }
                braking {
                  enabled
                  sensibility
                }
                cornering {
                  enabled
                  sensibility
                }
                impact {
                  enabled
                  sensibility
                }
              }
              medium {
                acceleration {
                  enabled
                  sensibility
                }
                braking {
                  enabled
                  sensibility
                }
                cornering {
                  enabled
                  sensibility
                }
                impact {
                  enabled
                  sensibility
                }
              }
              heavy {
                acceleration {
                  enabled
                  sensibility
                }
                braking {
                  enabled
                  sensibility
                }
                cornering {
                  enabled
                  sensibility
                }
                impact {
                  enabled
                  sensibility
                }
              }
            }
          }
        }
      }
    }
  `;

  private queryGetWifiSettings = gql`
    query queryGetFleetWifiSettings {
      wifiSettings {
        access_points {
          id
          ssid
          created_at
          created_by
        }
      }
    }
  `;

  private mutateUpdateFleetCameraConfigurationRequest = gql`
    mutation updateFleetEventsConfiguration(
      $input: UpdateEventsConfigurationInput!
    ) {
      updateFleetEventsConfiguration(input: $input)
    }
  `;

  private mutateUpdateCameraConfigurationRequest = gql`
    mutation updateCameraConfiguration(
      $input: [CameraConfigurationUpdateInput]!
    ) {
      changeCameraConfiguration(input: $input)
    }
  `;

  private mutateUpdateCameraSpeedLimitRequest = gql`
    mutation updateSpeedLimitConfiguration($input: SpeedLimitUpdateInput) {
      updateSpeedLimitConfiguration(input: $input)
    }
  `;

  public getCameraConfiguration = (page?: number, size?: number, sort?: Sort): Promise<PageableContent<CameraConfiguration>> =>
    graphQLRequester
      .query<{
        cameraConfiguration: PageableContentAPI<CameraConfigurationAPI>;
        errors: Array<any>;
      }>({
        query: this.queryGetCameraConfiguration,
        variables: {
          page,
          size,
          sort,
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return toCameraConfigurationPage(result.cameraConfiguration);
      });

  public getFleetCameraConfiguration = (): Promise<FleetEventsConfiguration> =>
    graphQLRequester
      .query<{
        fleetEventsConfiguration: FleetEventsConfiguration;
        errors: Array<any>;
      }>({
        query: this.queryGetFleetCameraConfiguration,
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return camelcaseKeys(result.fleetEventsConfiguration, { deep: true });
      });

  public updateFleetCameraConfiguration = (input: FleetCameraEventsConfigurationInput): Promise<boolean> =>
    graphQLRequester
      .mutate<{ updateFleetCameraConfiguration: boolean; errors: Array<any> }>({
        mutation: this.mutateUpdateFleetCameraConfigurationRequest,
        variables: {
          input: snakecaseKeys(input, { deep: true }),
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.updateFleetCameraConfiguration;
      });

  public updateCameraConfiguration = (updateCameraConfigurationRequest: UpdateCameraConfigurationRequest[]): Promise<boolean> =>
    graphQLRequester
      .mutate<{ updateCameraConfiguration: boolean; errors: Array<any> }>({
        mutation: this.mutateUpdateCameraConfigurationRequest,
        variables: {
          input: toUpdateCameraConfigurationRequestAPI(updateCameraConfigurationRequest),
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.updateCameraConfiguration;
      });

  public updateCameraSpeedLimit = (updateCameraSpeedLimitRequest: UpdateCameraSpeedLimit): Promise<boolean> =>
    graphQLRequester
      .mutate<{ updateCameraSpeedLimit: boolean; errors: Array<any> }>({
        mutation: this.mutateUpdateCameraSpeedLimitRequest,
        variables: {
          input: toUpdateSpeedLimitRequestAPI(updateCameraSpeedLimitRequest),
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.updateCameraSpeedLimit;
      });

  public getWifiSettings = (): Promise<WifiSettingsList> =>
    graphQLRequester
      .query<{
        wifiSettings: WifiSettingsList;
        errors: Array<any>;
      }>({
        query: this.queryGetWifiSettings,
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return camelcaseKeys(result.wifiSettings, { deep: true });
      });
}

export default GraphQLCameraConfigurationRequester;
